import React, { useEffect, useState } from "react";
import { FullScreenModal } from "components/FullScreenModal";
import { stringify } from "query-string";
import { ScrollArea } from "components/ScrollArea";
import { Typography } from "components/Typography";

import * as styles from "./MigrateToIOMLicenceModal.module.scss";
import cx from "classnames";
import { Button } from "components/Button";
import { NativeSelect } from "components/NativeSelect";
import { getAllCountries } from "utilities/address";
import { Form } from "components/Form";
import { Controller, useForm } from "react-hook-form";

import { useProfile } from "hooks/firestore/v2/user/useProfile";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { message } from "utilities/message";
import { toast, useDispatch, useHasPermission } from "hooks";
import { convertErrorToToast } from "utilities/errorsParser";
import { onLogout } from "utilities/User/Actions";
import * as support from "utilities/support";
import SkeletonParagraph from "components/Skeleton/SkeletonParagraph";
import {
  CustomerLicenceOptions,
  setZendeskTemp,
} from "utilities/Auth/authSlice";
import { setModal } from "utilities/UI/uiSlice";
import { useSelector } from "react-redux";
import type { RootState } from "store/createStore";
import { ACCOUNT_REDUX_EVENTS } from "types/Account";

const doArticleSearch = async (labels: string[]) => {
  const zendeskSearchUrl = `https://${ZENDESK_DOMAIN}/api/v2/help_center/articles/embeddable_search.json`;
  const query = {
    locale: "en-us",
    per_page: "20",
    origin: "web_widget",
    label_names: labels,
  };
  try {
    const response = await fetch(
      `${zendeskSearchUrl}?${stringify(query, { arrayFormat: "comma" })}`,
      {
        method: "GET",
      },
    );
    return await response.json();
  } catch (error) {
    toast(
      convertErrorToToast(error, {
        title: "Something went wrong",
      }),
    );
  }
};

const nationalityOptions = getAllCountries();

const schema = z.object({
  nationality: z
    .string({ required_error: "Please choose a nationality" })
    .min(2, "Please choose a nationality"),
});

export type FormData = z.infer<typeof schema>;

const MODAL_IDENTIFIER = "migrateToIOMLicence";
// IOM zendesk domains, use sandbox in staging - hardcoded as this is specific to IOM and shouldn't run for very long
const ZENDESK_DOMAIN =
  process.env.GATSBY_SKRILLA_ENV === "production"
    ? `picklebet-international.zendesk.com`
    : `picklebet-international1745290020.zendesk.com`;

const MigrateToIOMLicenceModal = () => {
  const dispatch = useDispatch();
  const [response, setResponse] = useState<{
    results: { body: string }[];
  } | null>(null);
  const { profile, isLoading: isLoadingProfile } = useProfile();

  // use the store to correctly release the body scroll on close
  const isOpen = useSelector(
    (state: RootState) => state.utilities.ui.modal === MODAL_IDENTIFIER,
  );

  const nationality = profile?.nationality || profile?.country || "";
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const shouldMigrateToIOMLicence = useHasPermission("migratedAccount", false);

  const {
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nationality,
    },
  });
  const nationalityFormValue = watch("nationality");

  useEffect(() => {
    // if it's not open, and it should be - open it
    if (!isOpen && shouldMigrateToIOMLicence) {
      dispatch(setModal(MODAL_IDENTIFIER));
      dispatch(setZendeskTemp({ zendeskDomain: ZENDESK_DOMAIN }));
      return;
    }
    // if it's open, and it shouldn't be - close it
    if (isOpen && !shouldMigrateToIOMLicence) {
      dispatch(setModal(null));

      // reset zendesk domains too
      dispatch(setZendeskTemp({ zendeskDomain: "" }));
    }
  }, [shouldMigrateToIOMLicence, isOpen]);

  useEffect(() => {
    if (nationality !== nationalityFormValue) {
      setValue("nationality", nationality || "");
    }
  }, [nationality]);

  const onSubmit = async (data: FormData) => {
    const formData = {
      accepted: true,
      ...data,
      licence: CustomerLicenceOptions.ISLE_OF_MAN,
    };

    const [_response, error] = await message("accept-licence-terms", formData);
    if (error) {
      toast(
        convertErrorToToast(error, {
          title: "Something went wrong",
        }),
      );
      return;
    }
    dispatch({
      type: ACCOUNT_REDUX_EVENTS.ACCEPTED_LICENCE_TERMS,
      payload: {
        licence: CustomerLicenceOptions.ISLE_OF_MAN,
        accepted: true,
      },
    });

    await handleClose(false);

    // reload so we get updated permissions
    window.location.reload();
  };

  const hasFormErrors = (Object.keys(errors)?.length || 0) >= 1;

  useEffect(() => {
    setIsLoadingContent(true);
    if (!shouldMigrateToIOMLicence) {
      setIsLoadingContent(false);
      return;
    }
    // guard against setting if component is unmounted
    let isMounted = true;

    (async () => {
      const data = await doArticleSearch(["weblink-terms-and-conditions"]);

      if (!isMounted) {
        return;
      }
      setResponse(data);
      setIsLoadingContent(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [shouldMigrateToIOMLicence]);

  const content =
    response?.results?.[0]?.body ||
    "An error occurred while getting the terms, please contact our support team.";
  const handleClose = async (logout = true) => {
    if (logout) {
      await dispatch(onLogout());
    }
    dispatch(setModal(null));
  };

  const handleSupportLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    support.openWithChat();
  };

  const isLoading = isLoadingProfile || isLoadingContent;

  return (
    <FullScreenModal
      onClose={handleClose}
      isOpen={isOpen}
      className={styles.modal}
      contentClassName={styles.modalContent}
    >
      <Typography variant={"h1"}>Updated Terms</Typography>
      <div className={styles.infoText}>
        <Typography variant="body" className={styles.body}>
          By accepting these updated terms, you'll be eligible to access
          exciting new features, including our casino offering, live betting,
          and expanded payment options, when they launch later this year.
        </Typography>
        <Typography variant="body" className={styles.body}>
          Your existing balance and any pending bets will remain unchanged. You
          can continue using your account to deposit, bet and withdraw.
        </Typography>
        <Typography variant="body" className={styles.body}>
          If you decide against accepting these terms, please{" "}
          <a href="#" onClick={handleSupportLink}>
            contact our support team
          </a>{" "}
          to arrange a withdrawal of your balance (if applicable) and close your
          account.
        </Typography>
      </div>

      <Typography component="label" className={styles.label} noSpacing>
        Terms and Conditions
      </Typography>
      <ScrollArea className={cx(styles.scrollArea)}>
        <Typography variant={`body`} className={styles.body}>
          {!isLoading && content ? (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          ) : (
            <SkeletonParagraph />
          )}
        </Typography>
      </ScrollArea>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="nationality"
          render={({ field }) => (
            <NativeSelect
              {...field}
              options={nationalityOptions}
              label={"Nationality"}
              errorMessage={errors?.nationality?.message}
              placeholder={"Select your nationality"}
              loading={isLoading}
            />
          )}
        />
        <div className={styles.actions}>
          <div>
            <Button variant={"secondary"} onClick={() => handleClose()}>
              Cancel
            </Button>
          </div>
          <div className={cx(styles.flexFill, styles.flexEnd)}>
            <Button
              type={`submit`}
              disabled={hasFormErrors}
              loading={isLoading}
              className={cx(styles.flexFill, styles.submitButton)}
            >
              Accept Terms
            </Button>
          </div>
        </div>
      </Form>
    </FullScreenModal>
  );
};

export { MigrateToIOMLicenceModal };
